import { createFileRoute } from "@tanstack/react-router"
import { useQuery } from "@tanstack/react-query"
import { Layout } from "@/components/layout"
import { Card, CardContent } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner"
import { Skeleton } from "@/components/ui/skeleton"
import { StoryReader } from "@/components/story-reader"
import { ReaderHeader, ReaderSettingsSheet } from "@/components/reader-settings"
import { StoryQuiz } from "@/components/story-quiz"
import { StoryGenerationPanel } from "@/components/admin/story-generation-panel"
import { StoryFeedback } from "@/components/story-feedback"
import { getStory } from "@/lib/api/stories"
import { storyLevelLabel } from "@/lib/story-level"
import { getKnowledgeItems } from "@/lib/api/knowledge"
import { useFuriganaPreference } from "@/hooks/use-furigana-preference"
import { useTranslationPreference } from "@/hooks/use-translation-preference"
import { useIsAdmin } from "@/hooks/use-is-admin"

export const Route = createFileRoute("/_authenticated/stories/$id")({
  component: Story,
})

function Story() {
  const { id } = Route.useParams()
  const { mode, setMode, firstOnly, setFirstOnly } = useFuriganaPreference()
  const { showTranslation, setShowTranslation } = useTranslationPreference()
  const isAdmin = useIsAdmin()

  const {
    data: story,
    isPending,
    isError,
  } = useQuery({
    queryKey: ["story", id],
    queryFn: () => getStory(id),
    // Poll while the quiz or the audio narration is still being generated in
    // the background; stop once both are present.
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return false
      return data.quiz === null || !data.audio ? 3000 : false
    },
  })

  const { data: knowledgeItems } = useQuery({
    queryKey: ["knowledge", "kanji"],
    queryFn: () => getKnowledgeItems({ type: "kanji", search: "" }),
    enabled: mode === "unknown",
  })

  const settings = {
    hasTranslation: Boolean(story?.translation),
    mode,
    onModeChange: setMode,
    firstOnly,
    onFirstOnlyChange: setFirstOnly,
    showTranslation,
    onShowTranslationChange: setShowTranslation,
  }

  return (
    <Layout
      back={{
        to: "/stories",
        label: "Stories",
        trailing: story && <ReaderSettingsSheet {...settings} />,
      }}
      tabBar={false}
    >
      <section className="pb-12">
        {/* The phone layout gets its back bar + settings sheet from Layout. */}
        <ReaderHeader
          className="hidden desktop:block"
          showSettings={Boolean(story)}
          {...settings}
        />
        {isPending && (
          <div className="space-y-4">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-9 w-3/4" />
            <Skeleton className="h-7 w-44" />
            <Card>
              <CardContent>
                <div className="space-y-3 py-2">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-5/6" />
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-4/5" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        {isError && (
          <p className="text-sm text-destructive">Failed to load story.</p>
        )}
        {story && (
          <article>
            <StoryReader
              title={story.title}
              subtitle={story.subtitle}
              levelLabel={storyLevelLabel(story)}
              audio={story.audio}
              html={story.html}
              translation={story.translation ?? null}
              mode={mode}
              firstOnly={firstOnly}
              showTranslation={showTranslation}
            />
            <div className="mt-3 flex justify-end">
              <StoryFeedback storyId={id} />
            </div>
            {story.quiz ? (
              <StoryQuiz
                storyId={id}
                questions={story.quiz}
                result={story.quizResult}
                furiganaMode={mode}
                furiganaFirstOnly={firstOnly}
                knownKanji={(knowledgeItems ?? []).map((item) => item.text)}
              />
            ) : (
              <div>
                <div className="mt-10 mb-6">
                  <p className="mb-1 text-sm tracking-widest text-muted-foreground uppercase">
                    Quiz
                  </p>
                  <h2 className="font-heading text-3xl font-semibold tracking-tight text-foreground">
                    クイズ
                  </h2>
                </div>
                <Spinner className="mt-6" size="lg" />
              </div>
            )}
            {isAdmin && <StoryGenerationPanel id={id} />}
          </article>
        )}
      </section>
    </Layout>
  )
}
